/**
 * Conjugation (*tiṅanta*) through the same Pāṇinian engine that drives
 * declension.
 *
 * Everything here goes through `deriveTinantas`, so each form arrives with its
 * prakriyā just as declined forms do. The root must be given in its *aupadeśika*
 * form — `ga\mx~`, not `gam` — because the it-markers and accents in it decide
 * the derivation. Passing a bare `gam` silently produces *gamati* instead of
 * *gacchati*.
 */

import {
    DerivedForm,
    PrakriyaStep,
    loadEngine,
    slp1ToIast,
} from "./vidyut-engine";
import {
    Dhatu,
    Lakara,
    Pada,
    Prayoga,
    Purusha,
    Vacana,
    PURUSHAS,
    VACANAS,
} from "./verb-types";

/** Minimal shape of the engine handle for conjugation. */
type TinantaEngine = {
    deriveTinantas(args: unknown): Array<{
        text: string;
        history: Array<{ rule: { code: string; source: string }; result: Array<{ text: string }> }>;
    }>;
};

const toArgs = (
    dhatu: Dhatu,
    lakara: Lakara,
    purusha: Purusha,
    vacana: Vacana,
    prayoga: Prayoga,
    pada?: Pada,
) => ({
    dhatu: {
        aupadeshika: dhatu.aupadeshika,
        gana: dhatu.gana,
        sanadi: [] as string[],
        prefixes: dhatu.prefixes ?? [],
    },
    lakara,
    prayoga,
    purusha,
    vacana,
    // The engine requires this field explicitly; false keeps the aṭ-augment
    // (a-gacchat) that the imperfect and aorist need.
    skip_at_agama: false,
    ...(pada ? { pada } : {}),
});

/** Derives every form for one cell of a conjugation table. */
export const conjugateCell = async (
    dhatu: Dhatu,
    lakara: Lakara,
    purusha: Purusha,
    vacana: Vacana,
    prayoga: Prayoga = "Kartari",
    pada?: Pada,
): Promise<DerivedForm[]> => {
    const engine = (await loadEngine()) as unknown as TinantaEngine | null;
    if (!engine) return [];

    let prakriyas;
    try {
        prakriyas = engine.deriveTinantas(toArgs(dhatu, lakara, purusha, vacana, prayoga, pada));
    } catch (e) {
        console.warn("vidyut: conjugation failed", dhatu, lakara, purusha, vacana, e);
        return [];
    }

    const seen = new Set<string>();
    const forms: DerivedForm[] = [];
    for (const p of prakriyas) {
        if (seen.has(p.text)) continue;
        seen.add(p.text);
        forms.push({
            text: slp1ToIast(p.text),
            slp1: p.text,
            steps: p.history.map((step): PrakriyaStep => ({
                code: step.rule.code,
                source: step.rule.source,
                terms: step.result.map(t => t.text).filter(t => t.length > 0).map(slp1ToIast),
            })),
        });
    }
    return forms;
};

/** A full 3x3 conjugation table for one lakāra. */
export type Conjugation = {
    dhatu: Dhatu;
    lakara: Lakara;
    prayoga: Prayoga;
    pada?: Pada;
    /** `cells[purusha][vacana]`. */
    cells: Record<Purusha, Record<Vacana, DerivedForm[]>>;
    /** True when no cell produced anything — the root does not take this lakāra. */
    empty: boolean;
};

/** Conjugates a root through one lakāra. */
export const conjugate = async (
    dhatu: Dhatu,
    lakara: Lakara,
    prayoga: Prayoga = "Kartari",
    pada?: Pada,
): Promise<Conjugation> => {
    const cells = {} as Record<Purusha, Record<Vacana, DerivedForm[]>>;
    let count = 0;

    for (const p of PURUSHAS) {
        cells[p.id] = {} as Record<Vacana, DerivedForm[]>;
        for (const v of VACANAS) {
            const forms = await conjugateCell(dhatu, lakara, p.id, v.id, prayoga, pada);
            cells[p.id][v.id] = forms;
            count += forms.length;
        }
    }

    return { dhatu, lakara, prayoga, pada, cells, empty: count === 0 };
};
